import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Calculator,
  Calendar,
  Check,
  ChevronDown,
  ChevronUp,
  DollarSign,
  Flag,
  HelpCircle,
  Infinity as InfinityIcon,
  Loader2,
  Lock,
  Minus,
  Plus,
  Settings,
  Users,
  X,
  Zap,
} from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AdPlacement } from "@/components/ads/AdPlacement";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { m } from "@/paraglide/messages";
import {
  AlgorithmInfo,
  AlgorithmSelector,
} from "../components/AlgorithmSelector";
import { F1Explainer } from "../components/F1Explainer";
import { MyTeamResults, MyTeamStatus } from "../components/MyTeamPanel";
import {
  BudgetPill,
  MobileTabBar,
  OptimalTeamCard,
} from "../components/mobile";

const OptimalTeamsTable = lazy(() =>
  import("../components/OptimalTeamsTable").then((m) => ({
    default: m.OptimalTeamsTable,
  })),
);

import {
  SeasonRaceSelector,
  useSeasonRaceState,
} from "../components/SeasonRaceSelector";
import { AvoidSection, TeamFiltersPanel } from "../components/TeamConstraints";

const TeamSelector = lazy(() =>
  import("../components/TeamSelector").then((m) => ({
    default: m.TeamSelector,
  })),
);

import {
  type BudgetBuilderStage,
  type Race,
  useRaceStages,
  useTeamCalculatorData,
} from "../hooks/use-f1-data";
import { useMyTeamVault } from "../hooks/use-my-team";
import { useOptimalTeamsWorker } from "../hooks/use-optimal-teams-worker";
import {
  useAlgorithms,
  useDefaultAlgorithmId,
  usePredictions,
} from "../hooks/use-predictions";
import type {
  ChipType,
  OptimalTeam,
  PredictedConstructor,
  PredictedDriver,
} from "../lib/algorithms";
import { canAffordPick } from "../lib/budget-range";
import {
  BUDGET_CAP,
  BUDGET_RANGE_FALLBACK,
  FANTASY_SEASONS,
} from "../lib/constants";
import {
  computeTransferSummary,
  findBaselineTeam,
  type SavedRaceTeam,
} from "../lib/my-team";
import { getTeamColor } from "../lib/team-colors";
import { hasActiveFilters, type TeamFilters } from "../lib/team-filters";

// Route API for reading URL search params
const routeApi = getRouteApi("/f1-fantasy/apex-team");

// Stage ID type
type StageId = BudgetBuilderStage;

// Slot types
type DriverSlots = [
  string | null,
  string | null,
  string | null,
  string | null,
  string | null,
];
type ConstructorSlots = [string | null, string | null];

// Chip label to ChipType mapping
const CHIP_MAP: Record<string, ChipType> = {
  x3: "x3",
  Limitless: "limitless",
  "No Neg": "no-negative",
};

const TEAMS_PER_PAGE = 20;

export function TeamCalculator() {
  // Read URL search params for persistence across reloads
  const {
    season: urlSeason,
    race: urlRaceSlug,
    stage: urlStage,
    budget: urlBudget,
  } = routeApi.useSearch();
  // Loader data: seasons + races prefetched server-side so we know the race
  // round on first render — used to derive earlySeasonMode without waiting
  // for the full teamCalcData fetch (eliminates CLS from banner appearing late).
  const loaderData = routeApi.useLoaderData();
  const navigate = useNavigate({ from: "/f1-fantasy/apex-team" });

  const [budget, setBudget] = useState(urlBudget ?? BUDGET_CAP);
  const [selectedChipLabel, setSelectedChipLabel] = useState<string | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [earlyWarningDismissed, setEarlyWarningDismissed] = useState(false);

  // Convert UI label to ChipType for algorithm
  const activeChip: ChipType = selectedChipLabel
    ? (CHIP_MAP[selectedChipLabel] ?? null)
    : null;
  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [manualStage, setManualStage] = useState<StageId | null>(null);

  // Algorithm selection
  const algorithms = useAlgorithms();
  const defaultAlgorithmId = useDefaultAlgorithmId();
  const [selectedAlgorithmId, setSelectedAlgorithmId] =
    useState<string>(defaultAlgorithmId);

  // Individual slot state (not arrays that shift)
  const [driverSlots, setDriverSlots] = useState<DriverSlots>([
    null,
    null,
    null,
    null,
    null,
  ]);
  const [constructorSlots, setConstructorSlots] = useState<ConstructorSlots>([
    null,
    null,
  ]);

  // Result filters + avoid list for the optimal-teams search
  const [teamFilters, setTeamFilters] = useState<TeamFilters>({});
  const [avoidDriverIds, setAvoidDriverIds] = useState<string[]>([]);
  const [avoidConstructorIds, setAvoidConstructorIds] = useState<string[]>([]);

  // Season/Race selection state using the shared hook, seeded from URL
  const {
    season: selectedSeason,
    setSeason: setSelectedSeason,
    race: selectedRace,
    setRace: setSelectedRace,
  } = useSeasonRaceState(urlSeason);

  // When the user changes season, selectedSeason (React state) updates immediately
  // but urlSeason (TanStack Router context) lags one render. During this gap,
  // urlRaceSlug is stale and must not be used as the initial race for the new season.
  const initRaceSlug =
    selectedSeason === (urlSeason ?? FANTASY_SEASONS[0])
      ? urlRaceSlug
      : undefined;

  // Close stage dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-stage-dropdown]")) {
        setStageDropdownOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Fetch race stages for stage selector
  const { data: stagesData } = useRaceStages(
    selectedSeason,
    selectedRace?.slug,
  );

  // Determine effective stage: manual selection > URL param > API current stage
  const effectiveStage: StageId = useMemo(() => {
    if (manualStage) return manualStage;
    if (urlStage) return urlStage as StageId;
    return (stagesData?.currentStage as StageId) ?? "pre_weekend";
  }, [manualStage, urlStage, stagesData?.currentStage]);

  // Reset manual stage when race changes (render-time state adjustment)
  const [prevRaceSlug, setPrevRaceSlug] = useState(selectedRace?.slug);
  if (selectedRace?.slug !== prevRaceSlug) {
    setPrevRaceSlug(selectedRace?.slug);
    setManualStage(null);
  }

  // Fetch team calculator data (raw history for client-side predictions)
  const { data: teamCalcData, isLoading: teamCalcLoading } =
    useTeamCalculatorData(selectedSeason, selectedRace?.slug, effectiveStage);

  // Derive earlySeasonMode as early as possible to avoid CLS.
  // Use actual value from teamCalcData when available; fall back to a round-based
  // estimate using the race round from the SSR-prefetched loader data.
  // earlySeasonMode is true when the selected race is round 1 or 2 of the season
  // (fewer than 2 prior races exist for rolling-average predictions).
  const currentRaceRound = selectedRace?.round ?? loaderData?.raceRound ?? 0;
  const earlySeasonMode =
    teamCalcData?.earlySeasonMode ?? currentRaceRound <= 2;

  // Apply prediction algorithm to raw data
  const {
    drivers: predictedDrivers,
    constructors: predictedConstructors,
    algorithmName,
    isPredictionMode,
  } = usePredictions(teamCalcData, selectedAlgorithmId);

  const isLoading = teamCalcLoading;

  // ── My Team: locally saved season team for signed-in users ──────────────
  const myTeam = useMyTeamVault(selectedSeason);
  const { signedIn, isLoaded: myTeamLoaded, vault } = myTeam;

  // Slot-ordered slugs (nulls preserved) — slugs are the stable persistence
  // key; slot DB ids are not stable across re-seeds.
  const slotDriverSlugs = useMemo(
    () =>
      driverSlots.map((id) =>
        id
          ? (predictedDrivers.find((d) => d.id === id)?.driverSlug ?? null)
          : null,
      ),
    [driverSlots, predictedDrivers],
  );
  const slotConstructorSlugs = useMemo(
    () =>
      constructorSlots.map((id) =>
        id
          ? (predictedConstructors.find((c) => c.id === id)?.slug ?? null)
          : null,
      ),
    [constructorSlots, predictedConstructors],
  );

  const currentRaceSnapshot =
    (selectedRace && vault?.races[selectedRace.slug]) || null;
  const baselineTeam = useMemo(
    () => (selectedRace ? findBaselineTeam(vault, selectedRace.round) : null),
    [vault, selectedRace],
  );

  // Live transfer allowance vs the previous race's saved line-up.
  const transferSummary = useMemo(() => {
    if (!signedIn || !selectedRace) return null;
    return computeTransferSummary(
      vault,
      selectedRace.round,
      slotDriverSlugs,
      slotConstructorSlugs,
      selectedChipLabel,
    );
  }, [
    signedIn,
    selectedRace,
    vault,
    slotDriverSlugs,
    slotConstructorSlugs,
    selectedChipLabel,
  ]);

  // Restore the saved team when entering a race: the exact snapshot for this
  // race if one exists, otherwise the line-up carried forward from the most
  // recent previous race. Runs once per race, and only into empty slots so
  // in-session edits are never clobbered.
  const restoredRaceRef = useRef<string | null>(null);
  useEffect(() => {
    const raceSlug = selectedRace?.slug;
    if (!raceSlug || !signedIn || !myTeamLoaded) return;
    if (predictedDrivers.length === 0 || predictedConstructors.length === 0)
      return;
    if (restoredRaceRef.current === raceSlug) return;
    restoredRaceRef.current = raceSlug;
    if (driverSlots.some(Boolean) || constructorSlots.some(Boolean)) return;
    const exact = vault?.races[raceSlug] ?? null;
    const snapshot =
      exact ??
      (selectedRace ? findBaselineTeam(vault, selectedRace.round) : null);
    if (!snapshot) return;
    setDriverSlots(
      Array.from({ length: 5 }, (_, i) => {
        const slug = snapshot.drivers[i] ?? null;
        return slug
          ? (predictedDrivers.find((d) => d.driverSlug === slug)?.id ?? null)
          : null;
      }) as DriverSlots,
    );
    setConstructorSlots(
      Array.from({ length: 2 }, (_, i) => {
        const slug = snapshot.constructors[i] ?? null;
        return slug
          ? (predictedConstructors.find((c) => c.slug === slug)?.id ?? null)
          : null;
      }) as ConstructorSlots,
    );
    // Chips are one-use per race: only restore for this race's own snapshot,
    // never when carrying a line-up forward.
    if (exact) setSelectedChipLabel(snapshot.chip);
  }, [
    selectedRace,
    signedIn,
    myTeamLoaded,
    vault,
    predictedDrivers,
    predictedConstructors,
    driverSlots,
    constructorSlots,
  ]);

  // Auto-save the current line-up for this race (debounced). Clearing every
  // slot removes the race's snapshot instead of saving an empty team.
  const { saveRaceTeam, removeRaceTeam } = myTeam;
  useEffect(() => {
    const raceSlug = selectedRace?.slug;
    const raceRound = selectedRace?.round;
    if (!raceSlug || raceRound === undefined || !signedIn || !myTeamLoaded)
      return;
    // Wait until the restore pass for this race has run.
    if (restoredRaceRef.current !== raceSlug) return;
    // Every selected id must have resolved to a slug (data fully loaded).
    if (
      driverSlots.filter(Boolean).length !==
        slotDriverSlugs.filter(Boolean).length ||
      constructorSlots.filter(Boolean).length !==
        slotConstructorSlugs.filter(Boolean).length
    )
      return;
    const hasSelection =
      slotDriverSlugs.some(Boolean) || slotConstructorSlugs.some(Boolean);
    const timer = setTimeout(() => {
      if (!hasSelection) {
        removeRaceTeam(raceSlug);
        return;
      }
      const next: SavedRaceTeam = {
        raceSlug,
        raceName: selectedRace?.name ?? raceSlug,
        raceRound,
        drivers: slotDriverSlugs,
        constructors: slotConstructorSlugs,
        chip: selectedChipLabel,
        budget,
        savedAt: Date.now(),
      };
      const existing = vault?.races[raceSlug];
      if (
        existing &&
        JSON.stringify({ ...existing, savedAt: 0 }) ===
          JSON.stringify({ ...next, savedAt: 0 })
      )
        return;
      saveRaceTeam(next);
    }, 600);
    return () => clearTimeout(timer);
  }, [
    selectedRace,
    signedIn,
    myTeamLoaded,
    vault,
    driverSlots,
    constructorSlots,
    slotDriverSlugs,
    slotConstructorSlugs,
    selectedChipLabel,
    budget,
    saveRaceTeam,
    removeRaceTeam,
  ]);

  // Post-race review: penalty is resolved from the saved line-up itself.
  const savedRacePenalty = useMemo(() => {
    if (!currentRaceSnapshot) return 0;
    return computeTransferSummary(
      vault,
      currentRaceSnapshot.raceRound,
      currentRaceSnapshot.drivers,
      currentRaceSnapshot.constructors,
      currentRaceSnapshot.chip,
    ).penaltyPoints;
  }, [vault, currentRaceSnapshot]);

  // Get selected IDs for display
  const selectedDriverIds = driverSlots.filter(Boolean) as string[];
  const selectedConstructorIds = constructorSlots.filter(Boolean) as string[];

  // Map selected IDs to driverSlugs for filtering in OptimalTeamsTable
  const selectedDriverSlugs = useMemo(() => {
    if (predictedDrivers.length === 0) return [];
    return selectedDriverIds
      .map((id) => {
        const result = predictedDrivers.find((d) => d.id === id);
        return result?.driverSlug;
      })
      .filter((slug): slug is string => !!slug);
  }, [selectedDriverIds, predictedDrivers]);

  // Map selected constructor IDs to slugs for filtering
  const selectedConstructorSlugs = useMemo(() => {
    if (predictedConstructors.length === 0) return [];
    return selectedConstructorIds
      .map((id) => {
        const result = predictedConstructors.find((c) => c.id === id);
        return result?.slug;
      })
      .filter((slug): slug is string => !!slug);
  }, [selectedConstructorIds, predictedConstructors]);

  // Derive which driver the user pinned into the x2/x3 multiplier slots.
  // Slot layout (mirrors TeamSelector):
  //   x3 chip active  → slot 0 = ×3 driver, slot 1 = ×2 driver
  //   no x3 chip      → slot 0 = ×2 driver
  const requiredX2DriverSlug = useMemo(() => {
    const x2SlotIndex = activeChip === "x3" ? 1 : 0;
    const id = driverSlots[x2SlotIndex];
    if (!id || predictedDrivers.length === 0) return null;
    return predictedDrivers.find((d) => d.id === id)?.driverSlug ?? null;
  }, [driverSlots, predictedDrivers, activeChip]);

  const requiredX3DriverSlug = useMemo(() => {
    if (activeChip !== "x3") return null;
    const id = driverSlots[0]; // slot 0 = ×3 driver when x3 chip active
    if (!id || predictedDrivers.length === 0) return null;
    return predictedDrivers.find((d) => d.id === id)?.driverSlug ?? null;
  }, [driverSlots, predictedDrivers, activeChip]);

  // Avoid list as slugs for the optimizer (ids are per-race DB records)
  const avoidDriverSlugs = useMemo(
    () =>
      avoidDriverIds
        .map((id) => predictedDrivers.find((d) => d.id === id)?.driverSlug)
        .filter((slug): slug is string => !!slug),
    [avoidDriverIds, predictedDrivers],
  );
  const avoidConstructorSlugs = useMemo(
    () =>
      avoidConstructorIds
        .map((id) => predictedConstructors.find((c) => c.id === id)?.slug)
        .filter((slug): slug is string => !!slug),
    [avoidConstructorIds, predictedConstructors],
  );

  // Calculate optimal teams using Web Worker
  // We fetch a reasonable max to support pagination without overwhelming memory
  const MAX_TEAMS_TO_FETCH = 1000;
  const {
    teams: allOptimalTeams,
    isCalculating,
    progress,
    totalTeamsFound,
    totalCombinationsEvaluated,
  } = useOptimalTeamsWorker({
    drivers: predictedDrivers,
    constructors: predictedConstructors,
    budget,
    maxTeams: MAX_TEAMS_TO_FETCH,
    requiredDriverSlugs: selectedDriverSlugs,
    requiredConstructorSlugs: selectedConstructorSlugs,
    activeChip,
    algorithmId: selectedAlgorithmId,
    algorithmName,
    enabled: predictedDrivers.length > 0 && predictedConstructors.length > 0,
    requiredX2DriverSlug,
    requiredX3DriverSlug,
    excludedDriverSlugs: avoidDriverSlugs,
    excludedConstructorSlugs: avoidConstructorSlugs,
    teamFilters,
  });

  // Pagination calculations
  const totalPages = Math.ceil(
    Math.min(totalTeamsFound, MAX_TEAMS_TO_FETCH) / TEAMS_PER_PAGE,
  );
  const startIndex = (currentPage - 1) * TEAMS_PER_PAGE;
  const endIndex = startIndex + TEAMS_PER_PAGE;
  const optimalTeams = useMemo(
    () => allOptimalTeams.slice(startIndex, endIndex),
    [allOptimalTeams, startIndex, endIndex],
  );

  // Reset to page 1 when inputs change (render-time state adjustment)
  const pageInputsKey = `${selectedAlgorithmId}-${budget}-${activeChip}-${selectedDriverSlugs.length}-${selectedConstructorSlugs.length}`;
  const [prevPageInputsKey, setPrevPageInputsKey] = useState(pageInputsKey);
  if (pageInputsKey !== prevPageInputsKey) {
    setPrevPageInputsKey(pageInputsKey);
    setCurrentPage(1);
  }

  // Page change handler
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top of optimal teams section (works on both mobile and desktop)
    // Use requestAnimationFrame to ensure DOM has updated before scrolling
    requestAnimationFrame(() => {
      const tableEl = document.querySelector("[data-optimal-teams-table]");
      if (tableEl) {
        // Dynamically measure all sticky headers to compute correct offset
        const stickyEls = document.querySelectorAll(
          'header, [class*="sticky"]',
        );
        let stickyHeight = 0;
        for (const el of stickyEls) {
          const style = getComputedStyle(el);
          if (style.position === "sticky" || style.position === "fixed") {
            stickyHeight = Math.max(
              stickyHeight,
              el.getBoundingClientRect().bottom,
            );
          }
        }
        const rect = tableEl.getBoundingClientRect();
        const padding = 12; // Small visual breathing room
        window.scrollTo({
          top: window.scrollY + rect.top - stickyHeight - padding,
          behavior: "smooth",
        });
      }
    });
  }, []);

  // Browse tab sort state (must be declared before displayDrivers/displayConstructors useMemos)
  const [sortField, setSortField] = useState<
    "price" | "priceChange" | "pts" | "value"
  >("pts");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const handleSortClick = (
    field: "price" | "priceChange" | "pts" | "value",
  ) => {
    if (sortField === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  // Transform data for display lists (sidebar + mobile browse)
  const displayDrivers = useMemo(() => {
    if (predictedDrivers.length === 0) return [];
    const mapped = predictedDrivers.map((d) => ({
      id: d.id,
      name: d.fullName,
      price: d.currentPrice,
      priceChange: d.predictedPriceChange,
      racePoints: d.predictedRacePoints,
      // Value = points per $1M (the core fantasy value-for-money metric)
      value: d.currentPrice > 0 ? d.predictedRacePoints / d.currentPrice : 0,
      color: getTeamColor(d.constructorSlug),
    }));
    return mapped.sort((a, b) => {
      const pick = (x: (typeof mapped)[number]) =>
        sortField === "price"
          ? x.price
          : sortField === "priceChange"
            ? x.priceChange
            : sortField === "value"
              ? x.value
              : x.racePoints;
      return sortDir === "desc" ? pick(b) - pick(a) : pick(a) - pick(b);
    });
  }, [predictedDrivers, sortField, sortDir]);

  const displayConstructors = useMemo(() => {
    if (predictedConstructors.length === 0) return [];
    const mapped = predictedConstructors.map((c) => ({
      id: c.id,
      name: c.name,
      price: c.currentPrice,
      priceChange: c.predictedPriceChange,
      racePoints: c.predictedRacePoints,
      // Value = points per $1M (the core fantasy value-for-money metric)
      value: c.currentPrice > 0 ? c.predictedRacePoints / c.currentPrice : 0,
      color: getTeamColor(c.slug),
    }));
    return mapped.sort((a, b) => {
      const pick = (x: (typeof mapped)[number]) =>
        sortField === "price"
          ? x.price
          : sortField === "priceChange"
            ? x.priceChange
            : sortField === "value"
              ? x.value
              : x.racePoints;
      return sortDir === "desc" ? pick(b) - pick(a) : pick(a) - pick(b);
    });
  }, [predictedConstructors, sortField, sortDir]);

  // Slot change handlers - update specific slot. Selecting an asset also
  // drops it from the avoid list (a pick always wins over an avoid).
  const handleDriverSlotChange = (
    slotIndex: 0 | 1 | 2 | 3 | 4,
    id: string | null,
  ) => {
    setDriverSlots((prev) => {
      const newSlots = [...prev] as DriverSlots;
      newSlots[slotIndex] = id;
      return newSlots;
    });
    if (id) setAvoidDriverIds((prev) => prev.filter((x) => x !== id));
  };

  const handleConstructorSlotChange = (slotIndex: 0 | 1, id: string | null) => {
    setConstructorSlots((prev) => {
      const newSlots = [...prev] as ConstructorSlots;
      newSlots[slotIndex] = id;
      return newSlots;
    });
    if (id) setAvoidConstructorIds((prev) => prev.filter((x) => x !== id));
  };

  // Click handler for sidebar items - find first empty slot or toggle if selected
  const handleDriverListClick = (id: string) => {
    const existingSlotIndex = driverSlots.indexOf(id);
    if (existingSlotIndex !== -1) {
      // Already selected - remove it
      handleDriverSlotChange(existingSlotIndex as 0 | 1 | 2 | 3 | 4, null);
    } else {
      // Find first empty slot
      const emptySlotIndex = driverSlots.indexOf(null);
      if (emptySlotIndex !== -1) {
        handleDriverSlotChange(emptySlotIndex as 0 | 1 | 2 | 3 | 4, id);
      }
    }
  };

  const handleConstructorListClick = (id: string) => {
    const existingSlotIndex = constructorSlots.indexOf(id);
    if (existingSlotIndex !== -1) {
      // Already selected - remove it
      handleConstructorSlotChange(existingSlotIndex as 0 | 1, null);
    } else {
      // Find first empty slot
      const emptySlotIndex = constructorSlots.indexOf(null);
      if (emptySlotIndex !== -1) {
        handleConstructorSlotChange(emptySlotIndex as 0 | 1, id);
      }
    }
  };

  // Load a suggested optimal team into the selector slots. Slot order encodes
  // captaincy: slot 0 = ×3 driver when the x3 chip is active, otherwise the
  // ×2 captain (matching TeamSelector's layout).
  const applyOptimalTeam = useCallback(
    (team: OptimalTeam) => {
      const captainSlugs = [
        ...(team.x3DriverSlug ? [team.x3DriverSlug] : []),
        team.x2DriverSlug,
      ];
      const rest = team.drivers.filter((s) => !captainSlugs.includes(s));
      const orderedSlugs = [...captainSlugs, ...rest].slice(0, 5);
      setDriverSlots(
        Array.from({ length: 5 }, (_, i) => {
          const slug = orderedSlugs[i];
          return slug
            ? (predictedDrivers.find((d) => d.driverSlug === slug)?.id ?? null)
            : null;
        }) as DriverSlots,
      );
      setConstructorSlots(
        team.constructors.map(
          (slug) =>
            predictedConstructors.find((c) => c.slug === slug)?.id ?? null,
        ) as ConstructorSlots,
      );
    },
    [predictedDrivers, predictedConstructors],
  );

  // Budget range is computed server-side from the FULL season's prices (the
  // drivers' raceHistory only covers the last 10 races — see computeBudgetRange).
  const budgetMin = teamCalcData?.budgetMin ?? BUDGET_RANGE_FALLBACK.min;
  const budgetMax = teamCalcData?.budgetMax ?? BUDGET_RANGE_FALLBACK.max;

  // Budget handlers
  const handleBudgetChange = useCallback(
    (value: number) => {
      // Round to 1 decimal place
      const rounded = Math.round(value * 10) / 10;
      const clamped = Math.max(budgetMin, Math.min(budgetMax, rounded));
      setBudget(clamped);
      navigate({
        search: (prev) => ({ ...prev, budget: clamped }),
        replace: true,
      });
    },
    [budgetMin, budgetMax, navigate],
  );

  // Calculate current spending
  const currentSpending = useMemo(() => {
    let total = 0;
    for (const id of selectedDriverIds) {
      const driver = predictedDrivers.find((d) => d.id === id);
      if (driver) total += driver.currentPrice;
    }
    for (const id of selectedConstructorIds) {
      const team = predictedConstructors.find((c) => c.id === id);
      if (team) total += team.currentPrice;
    }
    return total;
  }, [
    selectedDriverIds,
    selectedConstructorIds,
    predictedDrivers,
    predictedConstructors,
  ]);

  // Handle race change - reset selections and persist to URL
  const handleRaceChange = (race: Race) => {
    // Guard: SeasonRaceSelector fires onRaceChange on initialization.
    // Compare with current selectedRace or urlRaceSlug to detect real changes.
    const isNewRace = race.slug !== (selectedRace?.slug ?? urlRaceSlug ?? null);
    setSelectedRace(race);
    if (isNewRace) {
      // Clear selections when race actually changes (avoid-list ids are
      // per-race DB records, so they reset too)
      setDriverSlots([null, null, null, null, null]);
      setConstructorSlots([null, null]);
      setAvoidDriverIds([]);
      setAvoidConstructorIds([]);
    }
    navigate({
      search: (prev) => ({
        ...prev,
        race: race.slug,
        ...(isNewRace ? { stage: undefined } : {}),
      }),
      replace: true,
    });
  };

  // Handle season change - persist to URL and clear race+stage
  const handleSeasonChange = (season: number) => {
    setSelectedSeason(season);
    setSelectedRace(null); // clear immediately so SeasonRaceSelector auto-picks for new season
    navigate({ search: { season }, replace: true });
  };

  // Get selected algorithm for info display
  const selectedAlgorithm = useMemo(() => {
    return algorithms.find((a) => a.id === selectedAlgorithmId);
  }, [algorithms, selectedAlgorithmId]);

  // Mobile tab state
  const [mobileTabIndex, setMobileTabIndex] = useState(0);
  const mobileTabs = [
    {
      id: "setup",
      label: m.f1_apex_tab_setup(),
      icon: <Settings className="h-4 w-4" />,
    },
    {
      id: "team",
      label: m.f1_apex_tab_my_team(),
      icon: <Users className="h-4 w-4" />,
    },
    {
      id: "browse",
      label: m.f1_apex_tab_multi_pick(),
      icon: <Flag className="h-4 w-4" />,
    },
  ];
  const mobileTabIds = mobileTabs.map((t) => t.id);
  const handleMobileTabChange = (tabId: string) => {
    const idx = mobileTabIds.indexOf(tabId);
    if (idx !== -1) {
      setMobileTabIndex(idx);
      window.scrollTo(0, 0);
    }
  };

  // Mobile browse tab — driver/constructor toggle
  const [mobileBrowseMode, setMobileBrowseMode] = useState<
    "drivers" | "constructors"
  >("drivers");

  // Create lookup maps for mobile cards
  const driverMap = useMemo(() => {
    const map = new Map<string, PredictedDriver>();
    for (const d of predictedDrivers) {
      map.set(d.driverSlug, d);
    }
    return map;
  }, [predictedDrivers]);

  const constructorMap = useMemo(() => {
    const map = new Map<string, PredictedConstructor>();
    for (const c of predictedConstructors) {
      map.set(c.slug, c);
    }
    return map;
  }, [predictedConstructors]);

  // Shared driver list item renderer (used by both desktop sidebar and mobile Browse tab)
  const renderDriverItem = (driver: {
    id: string;
    name: string;
    price: number;
    priceChange: number;
    racePoints: number;
    value: number;
    color: string;
  }) => {
    const isSelected = selectedDriverIds.includes(driver.id);
    const isAvoided = avoidDriverIds.includes(driver.id);
    const exceedsBudget =
      activeChip !== "limitless" &&
      !isSelected &&
      !canAffordPick(driver.price, budget, currentSpending);
    const canSelect = !exceedsBudget && selectedDriverIds.length < 5;

    return (
      <button
        key={driver.id}
        type="button"
        onClick={() =>
          (isSelected || canSelect) && handleDriverListClick(driver.id)
        }
        disabled={!isSelected && !canSelect}
        className={`w-full p-3 border-b border-border/50 last:border-0 transition-all text-left ${
          isSelected
            ? "bg-success/10"
            : exceedsBudget || selectedDriverIds.length >= 5
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-muted/30"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-1 h-8 rounded-full"
            style={{ backgroundColor: driver.color }}
          />
          <div className="flex-1 min-w-0">
            <div
              className={`font-medium text-sm truncate ${
                isAvoided
                  ? "line-through decoration-destructive/60 text-muted-foreground"
                  : "text-foreground"
              }`}
            >
              {driver.name}
            </div>
          </div>
          <div className="flex items-center shrink-0 font-mono text-xs">
            <span
              className={`w-[4.5rem] text-right ${exceedsBudget ? "text-destructive" : "text-foreground"} text-sm`}
            >
              ${driver.price.toFixed(1)}M
            </span>
            <span
              className={`w-10 text-right ${
                Math.abs(driver.priceChange) < 0.05
                  ? "text-muted-foreground"
                  : driver.priceChange > 0
                    ? "text-success"
                    : "text-destructive"
              }`}
            >
              {driver.priceChange > 0 ? "+" : ""}
              {driver.priceChange.toFixed(1)}
            </span>
            <span
              className={`w-12 text-right ${isPredictionMode ? "text-amber-700 dark:text-amber-400" : "text-foreground"}`}
            >
              {driver.racePoints >= 0 ? "+" : ""}
              {driver.racePoints.toFixed(1)}
            </span>
            <span className="w-12 text-right text-foreground">
              {driver.value.toFixed(1)}
            </span>
          </div>
          {isSelected && (
            <div className="p-1.5 rounded-full bg-success/20">
              <Check className="h-3.5 w-3.5 text-success" />
            </div>
          )}
        </div>
      </button>
    );
  };

  // Shared constructor list item renderer
  const renderConstructorItem = (team: {
    id: string;
    name: string;
    price: number;
    priceChange: number;
    racePoints: number;
    value: number;
    color: string;
  }) => {
    const isSelected = selectedConstructorIds.includes(team.id);
    const exceedsBudget =
      activeChip !== "limitless" &&
      !isSelected &&
      !canAffordPick(team.price, budget, currentSpending);
    const canSelect = !exceedsBudget && selectedConstructorIds.length < 2;

    return (
      <button
        key={team.id}
        type="button"
        onClick={() =>
          (isSelected || canSelect) && handleConstructorListClick(team.id)
        }
        disabled={!isSelected && !canSelect}
        className={`w-full p-3 border-b border-border/50 last:border-0 transition-all text-left ${
          isSelected
            ? "bg-success/10"
            : exceedsBudget || selectedConstructorIds.length >= 2
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-muted/30"
        }`}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-1 h-8 rounded-full"
            style={{ backgroundColor: team.color }}
          />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-foreground truncate">
              {team.name}
            </div>
          </div>
          <div className="flex items-center shrink-0 font-mono text-xs">
            <span
              className={`w-[4.5rem] text-right ${exceedsBudget ? "text-destructive" : "text-foreground"} text-sm`}
            >
              ${team.price.toFixed(1)}M
            </span>
            <span
              className={`w-10 text-right ${
                Math.abs(team.priceChange) < 0.05
                  ? "text-muted-foreground"
                  : team.priceChange > 0
                    ? "text-success"
                    : "text-destructive"
              }`}
            >
              {team.priceChange > 0 ? "+" : ""}
              {team.priceChange.toFixed(1)}
            </span>
            <span
              className={`w-12 text-right ${isPredictionMode ? "text-amber-700 dark:text-amber-400" : "text-foreground"}`}
            >
              {team.racePoints >= 0 ? "+" : ""}
              {team.racePoints.toFixed(1)}
            </span>
            <span className="w-12 text-right text-foreground">
              {team.value.toFixed(1)}
            </span>
          </div>
          {isSelected && (
            <div className="p-1.5 rounded-full bg-success/20">
              <Check className="h-3.5 w-3.5 text-success" />
            </div>
          )}
        </div>
      </button>
    );
  };

  // My Team panels — sign-in banner / transfer allowance, plus the post-race
  // result of the saved team. Shown above the team selector on both layouts.
  const myTeamPanels = (
    <div className="space-y-3">
      {effectiveStage === "after_race" &&
        signedIn &&
        currentRaceSnapshot &&
        teamCalcData && (
          <MyTeamResults
            snapshot={currentRaceSnapshot}
            drivers={teamCalcData.drivers}
            constructors={teamCalcData.constructors}
            penaltyPoints={savedRacePenalty}
            raceName={currentRaceSnapshot.raceName}
          />
        )}
      <MyTeamStatus
        signedIn={signedIn}
        summary={transferSummary}
        carriedFromRaceName={
          !currentRaceSnapshot && baselineTeam ? baselineTeam.raceName : null
        }
        hasSelection={
          selectedDriverIds.length > 0 || selectedConstructorIds.length > 0
        }
      />
    </div>
  );

  // Avoid list + result filters (shared between mobile and desktop layouts)
  const avoidSection = (
    <AvoidSection
      drivers={predictedDrivers}
      constructors={predictedConstructors}
      avoidDriverIds={avoidDriverIds}
      avoidConstructorIds={avoidConstructorIds}
      onAvoidDriversChange={setAvoidDriverIds}
      onAvoidConstructorsChange={setAvoidConstructorIds}
      selectedDriverIds={selectedDriverIds}
      selectedConstructorIds={selectedConstructorIds}
      isPredictionMode={isPredictionMode}
    />
  );
  const filtersPanel = (
    <TeamFiltersPanel
      filters={teamFilters}
      onChange={setTeamFilters}
      isPredictionMode={isPredictionMode}
    />
  );

  // Prediction mode color legend — shown in My Team + Browse tabs (mobile) and above grid (desktop)
  const predictionLegend = isPredictionMode ? (
    <div className="flex items-center justify-center gap-x-4 gap-y-1.5 flex-wrap px-4 py-2.5 rounded-lg bg-muted/20 border border-border/50 text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
        <span>
          <span className="text-amber-700 dark:text-amber-400 font-medium">
            {m.f1_apex_legend_amber()}
          </span>{" "}
          {m.f1_apex_legend_predicted_pts()}
        </span>
      </span>
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span className="w-2 h-2 rounded-full bg-success shrink-0" />
        <span>
          <span className="text-success font-medium">
            {m.f1_apex_legend_green()}
          </span>{" "}
          {m.f1_apex_legend_price_rise()}
        </span>
      </span>
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <span className="w-2 h-2 rounded-full bg-destructive shrink-0" />
        <span>
          <span className="text-destructive font-medium">
            {m.f1_apex_legend_red()}
          </span>{" "}
          {m.f1_apex_legend_price_drop()}
        </span>
      </span>
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            {m.f1_apex_how_it_works()}
          </button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{m.f1_apex_how_title()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-foreground/80">
            <p>{m.f1_apex_how_p1()}</p>
            <p>{m.f1_apex_how_p2()}</p>
            <p>
              <span className="font-mono font-semibold text-amber-700 dark:text-amber-400">
                {m.f1_apex_how_p3_label()}
              </span>{" "}
              {m.f1_apex_how_p3()}
            </p>
            <p>{m.f1_apex_how_p4()}</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  ) : null;

  // Stage selector dropdown (shared between mobile/desktop)
  const stageSelector = (
    <div className="relative" data-stage-dropdown>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setStageDropdownOpen(!stageDropdownOpen);
        }}
        disabled={!stagesData?.stages?.length}
        className="w-full flex items-center gap-1.5 px-2.5 py-2 rounded-lg bg-muted/50 border border-border text-sm hover:bg-muted/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed md:w-auto md:min-w-[160px] md:gap-2 md:px-3"
      >
        <Calendar className="h-4 w-4 text-primary flex-shrink-0" />
        <span className="font-medium flex-1 text-left truncate">
          {stagesData?.stages?.find((s) => s.id === effectiveStage)?.name ??
            m.f1_budget_stage_pre_weekend()}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
      </button>

      {stageDropdownOpen && stagesData?.stages && (
        <div className="absolute top-full left-0 md:right-0 md:left-auto mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-lg bg-card border border-border shadow-lg z-50 max-h-80 overflow-y-auto">
          <div className="p-2">
            {stagesData.isSprintWeekend && (
              <div className="px-3 py-1.5 mb-2 text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-500/10 rounded-md flex items-center gap-1.5">
                <Flag className="h-3 w-3" />
                {m.f1_apex_sprint_weekend()}
              </div>
            )}
            {stagesData.stages.map((stage) => {
              const isStageSelected = effectiveStage === stage.id;
              const isDisabled = !stage.isAvailable;

              return (
                <button
                  key={stage.id}
                  title={isDisabled ? m.f1_apex_stage_locked() : undefined}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      setManualStage(stage.id as StageId);
                      navigate({
                        search: (prev) => ({ ...prev, stage: stage.id }),
                        replace: true,
                      });
                      setStageDropdownOpen(false);
                    }
                  }}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                    isStageSelected
                      ? "bg-primary/20 text-primary"
                      : isDisabled
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-muted/50"
                  }`}
                >
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      {stage.name}
                      {isDisabled && (
                        <Lock className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {stage.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div suppressHydrationWarning>
      {/* ================================================================
			    MOBILE LAYOUT (below md:)
			    ================================================================ */}
      <div className="md:hidden">
        {/* Mobile Header */}
        <div className="relative border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          <div className="px-4 py-2.5 flex items-center gap-2">
            <Link
              to="/f1-fantasy"
              aria-label={m.common_back_to_f1()}
              className="p-1 -ml-1 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            <Calculator className="h-4 w-4 text-primary" />
            <span className="font-display text-base font-bold text-foreground">
              {m.f1_apex_title()}
            </span>
          </div>

          {/* Tab Bar */}
          <div className="px-4 pb-3">
            <MobileTabBar
              tabs={mobileTabs}
              activeTab={mobileTabIds[mobileTabIndex]}
              onTabChange={handleMobileTabChange}
            />
          </div>
        </div>

        {earlySeasonMode && !earlyWarningDismissed && (
          <div className="mx-4 mt-4 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
            <div className="flex items-start gap-2">
              <span className="flex-1">
                <span className="font-medium">
                  {m.f1_apex_early_season_label()}
                </span>{" "}
                {m.f1_apex_early_season_note()}
              </span>
              <button
                type="button"
                aria-label={m.common_dismiss_warning()}
                onClick={() => setEarlyWarningDismissed(true)}
                className="shrink-0 mt-0.5 text-amber-700/60 hover:text-amber-700 dark:text-amber-400/60 dark:hover:text-amber-400"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Intro text */}
        <div className="px-4 pt-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {m.f1_apex_intro()}
          </p>
        </div>

        {/* Tab Panels — only render active panel */}
        {mobileTabIndex === 0 && (
          /* Panel 1: Setup */
          <div className="px-4 py-6 space-y-6">
            {/* Season & Race */}
            <div className="space-y-2">
              <SeasonRaceSelector
                onSeasonChange={handleSeasonChange}
                onRaceChange={handleRaceChange}
                defaultSeason={selectedSeason}
                initialRaceSlug={initRaceSlug}
                filterSeasons={FANTASY_SEASONS}
                filterRacesToSelectable
                compact
              />
              {stageSelector}
            </div>
            {/* Budget */}
            <div className="p-4 rounded-xl border border-border bg-card/50">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="h-4 w-4 text-secondary" />
                <span className="text-sm font-medium">
                  {m.f1_apex_budget_cap()}
                </span>
              </div>
              {activeChip === "limitless" ? (
                <div className="flex items-center justify-center gap-2 py-4">
                  <InfinityIcon className="h-7 w-7 text-accent" />
                  <span className="font-display text-2xl font-bold text-accent">
                    {m.f1_chip_limitless()}
                  </span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-3">
                    <button
                      type="button"
                      aria-label={m.f1_apex_decrease_budget()}
                      onClick={() => handleBudgetChange(budget - 0.1)}
                      className="p-2.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <BudgetInput
                      value={budget}
                      onChange={handleBudgetChange}
                      min={budgetMin}
                      max={budgetMax}
                    />
                    <button
                      type="button"
                      aria-label={m.f1_apex_increase_budget_aria()}
                      onClick={() => handleBudgetChange(budget + 0.1)}
                      className="p-2.5 rounded-lg bg-muted hover:bg-muted/80 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <Slider
                    aria-label={m.f1_apex_budget_cap_aria()}
                    value={[budget]}
                    onValueChange={([value]) => handleBudgetChange(value)}
                    min={budgetMin}
                    max={budgetMax}
                    step={0.1}
                    className="w-full"
                  />
                  <div className="flex justify-between mt-1 text-xs text-muted-foreground font-mono">
                    <span>${budgetMin.toFixed(1)}M</span>
                    <span>${budgetMax.toFixed(1)}M</span>
                  </div>
                </>
              )}
            </div>

            {/* Chip Selection */}
            <div className="p-4 rounded-xl border border-border bg-card/50">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-accent" />
                <span className="text-sm font-medium">
                  {m.f1_apex_active_chip()}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {(
                  [
                    { label: "x3", desc: m.f1_apex_chip_x3_desc() },
                    {
                      label: m.f1_chip_limitless(),
                      desc: m.f1_apex_chip_limitless_desc(),
                    },
                    {
                      label: m.f1_chip_no_neg(),
                      desc: m.f1_apex_chip_no_neg_desc(),
                    },
                  ] as { label: string; desc: string }[]
                ).map(({ label, desc }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      setSelectedChipLabel(
                        selectedChipLabel === label ? null : label,
                      );
                    }}
                    className={`flex flex-col items-center justify-center gap-0.5 px-3 py-2 rounded-lg text-sm font-mono transition-all min-h-[52px] ${
                      selectedChipLabel === label
                        ? "chip-on-accent"
                        : "chip-idle hover:brightness-110"
                    }`}
                  >
                    {label}
                    <span
                      className={`text-[11px] font-sans leading-tight text-center ${
                        selectedChipLabel === label
                          ? "text-accent-foreground font-medium"
                          : "text-muted-foreground font-normal"
                      }`}
                    >
                      {desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Algorithm */}
            <div className="p-4 rounded-xl border border-border bg-card/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium">
                  {m.f1_apex_prediction_algorithm()}
                </span>
              </div>
              <AlgorithmSelector
                algorithms={algorithms}
                selectedId={selectedAlgorithmId}
                onSelect={setSelectedAlgorithmId}
                disabled={isLoading}
              />
              <div className="mt-3">
                <AlgorithmInfo algorithm={selectedAlgorithm} />
              </div>
            </div>
          </div>
        )}

        {mobileTabIndex === 1 && (
          /* Panel 2: My Team */
          <div className="px-4 py-6 space-y-6">
            {myTeamPanels}

            {/* Budget */}
            <BudgetPill
              currentSpending={currentSpending}
              budgetCap={budget}
              activeChip={activeChip}
            />

            {/* Team Selector */}
            <Suspense
              fallback={
                <div className="h-40 animate-pulse rounded-xl bg-muted/20" />
              }
            >
              <TeamSelector
                driverSlots={driverSlots}
                constructorSlots={constructorSlots}
                onDriverSlotChange={handleDriverSlotChange}
                onConstructorSlotChange={handleConstructorSlotChange}
                onClearAll={() => {
                  setDriverSlots([null, null, null, null, null]);
                  setConstructorSlots([null, null]);
                }}
                drivers={predictedDrivers}
                constructors={predictedConstructors}
                isLoading={isLoading}
                budgetCap={budget}
                activeChip={activeChip}
                isPredictionMode={isPredictionMode}
                x2Locked={false}
              />
            </Suspense>

            {/* Avoid list */}
            {avoidSection}

            {/* Prediction legend */}
            {predictionLegend}

            {/* Optimal Teams as cards */}
            {isCalculating ? (
              <div className="py-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
                <p className="text-sm text-muted-foreground mb-2">
                  {m.f1_apex_calculating()}
                </p>
                <div className="w-48 mx-auto h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-200"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ) : optimalTeams.length > 0 ? (
              <div className="space-y-2" data-optimal-teams-table>
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-display font-semibold text-foreground">
                    {m.f1_apex_optimal_teams()}
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      {totalTeamsFound.toLocaleString()} {m.f1_apex_valid()}
                    </span>
                    {filtersPanel}
                  </div>
                </div>
                {/* Top Pagination (compact) */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {m.f1_apex_page_of({
                        current: currentPage,
                        total: totalPages,
                      })}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-xs font-medium disabled:opacity-30 min-h-[36px]"
                      >
                        {m.f1_apex_prev()}
                      </button>
                      <button
                        type="button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 rounded-lg bg-muted/50 border border-border text-xs font-medium disabled:opacity-30 min-h-[36px]"
                      >
                        {m.f1_apex_next()}
                      </button>
                    </div>
                  </div>
                )}
                {/* Scrollable rows — sticky rank/pts column + horizontal badge scroll */}
                <div className="rounded-xl border border-border overflow-hidden">
                  <HScrollFade leftOffset={52}>
                    <div className="divide-y divide-border/30 bg-card/50">
                      {optimalTeams.map((team) => (
                        <OptimalTeamCard
                          key={team.rank}
                          team={team}
                          driverMap={driverMap}
                          constructorMap={constructorMap}
                          selectedDriverSlugs={selectedDriverSlugs}
                          selectedConstructorSlugs={selectedConstructorSlugs}
                          hasX3Chip={activeChip === "x3"}
                          isPredictionMode={isPredictionMode}
                          onApplyTeam={applyOptimalTeam}
                        />
                      ))}
                    </div>
                  </HScrollFade>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-4 py-2.5 rounded-lg bg-muted/50 border border-border text-sm font-medium disabled:opacity-30 min-h-[44px]"
                    >
                      {m.f1_apex_prev()}
                    </button>
                    <span className="text-sm text-muted-foreground">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2.5 rounded-lg bg-muted/50 border border-border text-sm font-medium disabled:opacity-30 min-h-[44px]"
                    >
                      {m.f1_apex_next()}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              !isLoading && (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  {predictedDrivers.length === 0
                    ? m.f1_apex_select_race_optimal()
                    : hasActiveFilters(teamFilters)
                      ? m.f1_apex_no_teams_filters()
                      : m.f1_apex_no_teams_budget()}
                </div>
              )
            )}
          </div>
        )}

        {mobileTabIndex === 2 && (
          /* Panel 3: Browse */
          <div className="px-4 py-6 space-y-4">
            {/* Drivers/Constructors toggle */}
            <MobileTabBar
              tabs={[
                {
                  id: "drivers",
                  label: m.f1_apex_drivers_count({
                    count: displayDrivers.length,
                  }),
                  icon: <Users className="h-4 w-4" />,
                },
                {
                  id: "constructors",
                  label: m.f1_apex_constructors_count({
                    count: displayConstructors.length,
                  }),
                  icon: <Flag className="h-4 w-4" />,
                },
              ]}
              activeTab={mobileBrowseMode}
              onTabChange={(id) =>
                setMobileBrowseMode(id as "drivers" | "constructors")
              }
            />

            {/* Prediction legend */}
            {predictionLegend}

            {/* List */}
            <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
              {/* Column headers as sort buttons */}
              {!isLoading &&
                (mobileBrowseMode === "drivers"
                  ? displayDrivers.length > 0
                  : displayConstructors.length > 0) && (
                  <div className="px-3 border-b border-border/50 bg-muted/60">
                    <div className="flex items-center gap-3">
                      <div className="w-1 shrink-0" />
                      <div className="flex-1 min-w-0" />
                      <div className="flex items-center shrink-0 font-mono text-xs">
                        {(
                          [
                            { field: "price", label: "$" },
                            {
                              field: "priceChange",
                              label: isPredictionMode ? "xΔ$" : "Δ$",
                            },
                            {
                              field: "pts",
                              label: isPredictionMode
                                ? m.f1_modal_col_xpts()
                                : m.f1_modal_col_pts(),
                            },
                            { field: "value", label: "PPM" },
                          ] as const
                        ).map(({ field, label }) => (
                          <button
                            key={field}
                            type="button"
                            title={
                              field === "priceChange"
                                ? isPredictionMode
                                  ? m.f1_apex_tooltip_expected_price_change()
                                  : m.f1_apex_tooltip_price_change()
                                : field === "pts"
                                  ? isPredictionMode
                                    ? m.f1_apex_tooltip_expected_fantasy_pts()
                                    : m.f1_apex_tooltip_fantasy_pts()
                                  : undefined
                            }
                            onClick={() => handleSortClick(field)}
                            className={`flex items-center justify-center gap-0.5 py-2.5 font-mono text-xs font-medium transition-colors ${
                              field === "price"
                                ? "w-[4.5rem]"
                                : field === "priceChange"
                                  ? "w-10"
                                  : "w-12"
                            } ${
                              sortField === field
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {label}
                            {sortField === field ? (
                              sortDir === "desc" ? (
                                <ChevronDown className="h-3 w-3 shrink-0" />
                              ) : (
                                <ChevronUp className="h-3 w-3 shrink-0" />
                              )
                            ) : null}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              {isLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                </div>
              ) : mobileBrowseMode === "drivers" ? (
                displayDrivers.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground text-sm">
                    {m.f1_apex_select_race_drivers()}
                  </div>
                ) : (
                  displayDrivers.map(renderDriverItem)
                )
              ) : displayConstructors.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  {m.f1_apex_select_race_constructors()}
                </div>
              ) : (
                displayConstructors.map(renderConstructorItem)
              )}
            </div>
          </div>
        )}
      </div>

      {/* ================================================================
			    DESKTOP LAYOUT (md: and above) — unchanged
			    ================================================================ */}
      <div className="hidden md:block">
        {/* Header */}
        <div className="relative border-b border-border bg-card/50 backdrop-blur-sm sticky top-16 z-40">
          {/* Bottom accent line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <Link
                  to="/f1-fantasy"
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span className="text-sm">{m.common_back()}</span>
                </Link>
                <div className="w-px h-6 bg-border" />
                <div className="flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-primary" />
                  <h1 className="font-display text-xl font-bold text-foreground">
                    {m.f1_apex_title()}
                  </h1>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <SeasonRaceSelector
                  onSeasonChange={handleSeasonChange}
                  onRaceChange={handleRaceChange}
                  defaultSeason={selectedSeason}
                  initialRaceSlug={initRaceSlug}
                  filterSeasons={FANTASY_SEASONS}
                  filterRacesToSelectable
                />
                {stageSelector}
                {isLoading && (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          {/* Early season warning banner */}
          {earlySeasonMode && !earlyWarningDismissed && (
            <div className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
              <div className="flex items-start gap-2">
                <span className="flex-1">
                  <span className="font-medium">
                    {m.f1_apex_early_season_label()}
                  </span>{" "}
                  {m.f1_apex_early_season_note()}
                </span>
                <button
                  type="button"
                  aria-label={m.common_dismiss_warning()}
                  onClick={() => setEarlyWarningDismissed(true)}
                  className="shrink-0 mt-0.5 text-amber-700/60 hover:text-amber-700 dark:text-amber-400/60 dark:hover:text-amber-400"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          {/* Intro text */}
          <div className="mb-6">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {m.f1_apex_intro()}
            </p>
          </div>

          {/* Settings Panel */}
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            {/* Budget */}
            <div className="p-4 rounded-xl border border-border bg-card/50">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="h-4 w-4 text-secondary" />
                <span className="text-sm font-medium text-foreground">
                  {m.f1_apex_budget_cap()}
                </span>
              </div>
              {activeChip === "limitless" ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex-1 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <InfinityIcon className="h-7 w-7 text-accent" />
                        <span className="font-display text-2xl font-bold text-accent">
                          {m.f1_chip_limitless()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="h-5 flex items-center justify-center">
                    <span className="text-sm text-muted-foreground">
                      {m.f1_apex_no_budget_restrictions()}
                    </span>
                  </div>
                  <div className="h-4 mt-1" />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      aria-label={m.f1_apex_decrease_budget()}
                      onClick={() => handleBudgetChange(budget - 0.1)}
                      className="p-1.5 rounded-md bg-muted hover:bg-muted/80 transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <BudgetInput
                      value={budget}
                      onChange={handleBudgetChange}
                      min={budgetMin}
                      max={budgetMax}
                    />
                    <button
                      type="button"
                      aria-label={m.f1_apex_increase_budget_aria()}
                      onClick={() => handleBudgetChange(budget + 0.1)}
                      className="p-1.5 rounded-md bg-muted hover:bg-muted/80 transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <Slider
                    aria-label={m.f1_apex_budget_cap_aria()}
                    value={[budget]}
                    onValueChange={([value]) => handleBudgetChange(value)}
                    min={budgetMin}
                    max={budgetMax}
                    step={0.1}
                    className="w-full"
                  />
                  <div className="flex justify-between mt-1 text-xs text-muted-foreground font-mono">
                    <span>${budgetMin.toFixed(1)}M</span>
                    <span>${budgetMax.toFixed(1)}M</span>
                  </div>
                </>
              )}
            </div>

            {/* Chip Selection */}
            <div className="p-4 rounded-xl border border-border bg-card/50">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="h-4 w-4 text-accent" />
                <span className="text-sm font-medium text-foreground">
                  {m.f1_apex_active_chip()}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(
                  [
                    { label: "x3", desc: m.f1_apex_chip_x3_desc() },
                    {
                      label: m.f1_chip_limitless(),
                      desc: m.f1_apex_chip_limitless_desc(),
                    },
                    {
                      label: m.f1_chip_no_neg(),
                      desc: m.f1_apex_chip_no_neg_desc(),
                    },
                  ] as { label: string; desc: string }[]
                ).map(({ label, desc }) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      setSelectedChipLabel(
                        selectedChipLabel === label ? null : label,
                      );
                    }}
                    className={`flex flex-col items-center justify-center gap-0.5 px-3 py-2 rounded-md text-xs font-mono transition-all min-h-[48px] ${
                      selectedChipLabel === label
                        ? "chip-on-accent"
                        : "chip-idle hover:brightness-110"
                    }`}
                  >
                    {label}
                    <span
                      className={`text-[11px] font-sans leading-tight text-center ${
                        selectedChipLabel === label
                          ? "text-accent-foreground font-medium"
                          : "text-muted-foreground font-normal"
                      }`}
                    >
                      {desc}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Algorithm Selection */}
            <div className="p-4 rounded-xl border border-border bg-card/50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-foreground">
                  {m.f1_apex_prediction_algorithm()}
                </span>
              </div>
              <AlgorithmSelector
                algorithms={algorithms}
                selectedId={selectedAlgorithmId}
                onSelect={setSelectedAlgorithmId}
                disabled={isLoading}
              />
              <div className="mt-3">
                <AlgorithmInfo algorithm={selectedAlgorithm} />
              </div>
            </div>
          </div>

          {/* My Team status / post-race results */}
          <div className="mb-6">{myTeamPanels}</div>

          {/* Team Selector */}
          <Suspense
            fallback={
              <div className="h-40 animate-pulse rounded-xl bg-muted/20" />
            }
          >
            <TeamSelector
              driverSlots={driverSlots}
              constructorSlots={constructorSlots}
              onDriverSlotChange={handleDriverSlotChange}
              onConstructorSlotChange={handleConstructorSlotChange}
              onClearAll={() => {
                setDriverSlots([null, null, null, null, null]);
                setConstructorSlots([null, null]);
              }}
              drivers={predictedDrivers}
              constructors={predictedConstructors}
              isLoading={isLoading}
              budgetCap={budget}
              activeChip={activeChip}
              isPredictionMode={isPredictionMode}
              x2Locked={false}
            />
          </Suspense>

          {/* Avoid list */}
          <div className="mt-4">{avoidSection}</div>

          {/* Prediction legend */}
          {predictionLegend}

          <AdPlacement className="my-6" />

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Optimal Teams Table */}
            <div className="lg:col-span-2" data-optimal-teams-table>
              <Suspense
                fallback={
                  <div className="h-64 animate-pulse rounded-xl bg-muted/20" />
                }
              >
                <OptimalTeamsTable
                  teams={optimalTeams}
                  drivers={predictedDrivers}
                  constructors={predictedConstructors}
                  selectedDriverSlugs={selectedDriverSlugs}
                  selectedConstructorSlugs={selectedConstructorSlugs}
                  isLoading={isLoading && !teamCalcData}
                  isCalculating={isCalculating}
                  progress={progress}
                  totalTeamsFound={totalTeamsFound}
                  totalCombinationsEvaluated={totalCombinationsEvaluated}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  activeChip={activeChip}
                  budget={budget}
                  isPredictionMode={isPredictionMode}
                  onApplyTeam={applyOptimalTeam}
                  hasFiltersActive={hasActiveFilters(teamFilters)}
                  filtersSlot={filtersPanel}
                />
              </Suspense>
            </div>

            {/* Sidebar - Driver/Constructor Lists */}
            <div className="space-y-6">
              {/* Drivers Panel */}
              <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
                <div className="px-3 border-b border-border bg-muted/60">
                  <div className="flex items-center gap-3">
                    <h2 className="font-display font-semibold text-foreground flex-1 min-w-0 truncate py-3">
                      {m.f1_apex_drivers()}
                    </h2>
                    {!isLoading && displayDrivers.length > 0 && (
                      <div className="flex items-center shrink-0 font-mono text-xs">
                        {(
                          [
                            { field: "price", label: "$" },
                            {
                              field: "priceChange",
                              label: isPredictionMode ? "xΔ$" : "Δ$",
                            },
                            {
                              field: "pts",
                              label: isPredictionMode
                                ? m.f1_modal_col_xpts()
                                : m.f1_modal_col_pts(),
                            },
                            { field: "value", label: "PPM" },
                          ] as const
                        ).map(({ field, label }) => (
                          <button
                            key={field}
                            type="button"
                            title={
                              field === "priceChange"
                                ? isPredictionMode
                                  ? m.f1_apex_tooltip_expected_price_change()
                                  : m.f1_apex_tooltip_price_change()
                                : field === "pts"
                                  ? isPredictionMode
                                    ? m.f1_apex_tooltip_expected_fantasy_pts()
                                    : m.f1_apex_tooltip_fantasy_pts()
                                  : undefined
                            }
                            onClick={() => handleSortClick(field)}
                            className={`flex items-center justify-center gap-0.5 py-3 font-mono text-xs font-medium transition-colors ${
                              field === "price"
                                ? "w-[4.5rem]"
                                : field === "priceChange"
                                  ? "w-10"
                                  : "w-12"
                            } ${
                              sortField === field
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {label}
                            {sortField === field ? (
                              sortDir === "desc" ? (
                                <ChevronDown className="h-3 w-3 shrink-0" />
                              ) : (
                                <ChevronUp className="h-3 w-3 shrink-0" />
                              )
                            ) : null}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="relative">
                  {isLoading ? (
                    <div className="p-8 text-center">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                    </div>
                  ) : displayDrivers.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">
                      {m.f1_apex_select_race_drivers()}
                    </div>
                  ) : (
                    <ScrollableList itemCount={displayDrivers.length}>
                      {displayDrivers.map(renderDriverItem)}
                    </ScrollableList>
                  )}
                </div>
              </div>

              <AdPlacement className="my-4" />

              {/* Constructors Panel */}
              <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
                <div className="px-3 border-b border-border bg-muted/60">
                  <div className="flex items-center gap-3">
                    <h2 className="font-display font-semibold text-foreground flex-1 min-w-0 truncate py-3">
                      {m.f1_apex_constructors()}
                    </h2>
                    {!isLoading && displayConstructors.length > 0 && (
                      <div className="flex items-center shrink-0 font-mono text-xs">
                        {(
                          [
                            { field: "price", label: "$" },
                            {
                              field: "priceChange",
                              label: isPredictionMode ? "xΔ$" : "Δ$",
                            },
                            {
                              field: "pts",
                              label: isPredictionMode
                                ? m.f1_modal_col_xpts()
                                : m.f1_modal_col_pts(),
                            },
                            { field: "value", label: "PPM" },
                          ] as const
                        ).map(({ field, label }) => (
                          <button
                            key={field}
                            type="button"
                            title={
                              field === "priceChange"
                                ? isPredictionMode
                                  ? m.f1_apex_tooltip_expected_price_change()
                                  : m.f1_apex_tooltip_price_change()
                                : field === "pts"
                                  ? isPredictionMode
                                    ? m.f1_apex_tooltip_expected_fantasy_pts()
                                    : m.f1_apex_tooltip_fantasy_pts()
                                  : undefined
                            }
                            onClick={() => handleSortClick(field)}
                            className={`flex items-center justify-center gap-0.5 py-3 font-mono text-xs font-medium transition-colors ${
                              field === "price"
                                ? "w-[4.5rem]"
                                : field === "priceChange"
                                  ? "w-10"
                                  : "w-12"
                            } ${
                              sortField === field
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {label}
                            {sortField === field ? (
                              sortDir === "desc" ? (
                                <ChevronDown className="h-3 w-3 shrink-0" />
                              ) : (
                                <ChevronUp className="h-3 w-3 shrink-0" />
                              )
                            ) : null}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="relative">
                  {isLoading ? (
                    <div className="p-8 text-center">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                    </div>
                  ) : displayConstructors.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground text-sm">
                      {m.f1_apex_select_race_constructors()}
                    </div>
                  ) : (
                    <ScrollableList itemCount={displayConstructors.length}>
                      {displayConstructors.map(renderConstructorItem)}
                    </ScrollableList>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <F1Explainer
        title={m.f1_ax_title()}
        howTo={m.f1_ax_howto()}
        faqs={[
          { q: m.f1_ax_faq1_q(), a: m.f1_ax_faq1_a() },
          { q: m.f1_ax_faq2_q(), a: m.f1_ax_faq2_a() },
          { q: m.f1_ax_faq3_q(), a: m.f1_ax_faq3_a() },
          { q: m.f1_ax_faq4_q(), a: m.f1_ax_faq4_a() },
        ]}
      />
      <AdPlacement className="mt-6" />
    </div>
  );
}

// ============================================================================
// ScrollableList Component with smart scroll indicator
// ============================================================================

interface ScrollableListProps {
  children: React.ReactNode;
  itemCount: number;
  maxHeight?: string;
}

function ScrollableList({
  children,
  itemCount,
  maxHeight = "320px",
}: ScrollableListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<"top" | "middle" | "bottom">(
    "top",
  );
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if content is scrollable (more items than fit in view)
  const [isScrollable, setIsScrollable] = useState(false);

  // Update scroll state based on position
  const updateScrollState = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollableDistance = scrollHeight - clientHeight;

    // Check if scrollable
    setIsScrollable(scrollableDistance > 10);

    if (scrollableDistance <= 10) {
      setScrollState("top");
    } else if (scrollTop < 10) {
      setScrollState("top");
    } else if (scrollTop >= scrollableDistance - 10) {
      setScrollState("bottom");
    } else {
      setScrollState("middle");
    }
  }, []);

  // Handle scroll event
  const handleScroll = useCallback(() => {
    // Mark as scrolling
    setIsScrolling(true);

    // Clear previous timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Update scroll position
    updateScrollState();

    // Show indicator again after 150ms of no scrolling
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 150);
  }, [updateScrollState]);

  // Initialize scroll state and observe resize
  useEffect(() => {
    updateScrollState();

    const container = containerRef.current;
    if (!container) return;

    // Observe size changes
    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [updateScrollState, itemCount]);

  // Scroll helper for clicking the indicator
  const handleIndicatorClick = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (scrollState === "top" || scrollState === "middle") {
      // Scroll down
      container.scrollBy({ top: 150, behavior: "smooth" });
    } else {
      // Scroll up
      container.scrollBy({ top: -150, behavior: "smooth" });
    }
  }, [scrollState]);

  const showIndicator = isScrollable && !isScrolling;

  return (
    <div className="relative">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="overflow-y-auto"
        style={{ maxHeight }}
      >
        {children}
      </div>

      {/* Scroll indicator */}
      {showIndicator && (
        <button
          type="button"
          onClick={handleIndicatorClick}
          className="absolute bottom-2 left-1/2 -translate-x-1/2 p-1.5 rounded-full bg-muted/90 border border-border/50 shadow-sm hover:bg-muted transition-all animate-in fade-in duration-150"
          aria-label={
            scrollState === "bottom"
              ? m.f1_apex_scroll_up()
              : m.f1_apex_scroll_down()
          }
        >
          {scrollState === "bottom" ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      )}
    </div>
  );
}

// ============================================================================
// HScrollFade — horizontal scroll container with right-edge fade indicator
// ============================================================================

function HScrollFade({
  children,
  leftOffset = 0,
}: {
  children: React.ReactNode;
  leftOffset?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const hasOverflow = el.scrollWidth > el.clientWidth + 4;
    const atEnd = el.scrollWidth - el.scrollLeft - el.clientWidth < 10;
    setShowLeft(el.scrollLeft > 10);
    setShowRight(hasOverflow && !atEnd);
  }, []);

  useEffect(() => {
    check();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [check]);

  return (
    <div className="relative">
      <div ref={ref} onScroll={check} className="overflow-x-auto">
        {children}
      </div>
      {showLeft && (
        <div
          className="absolute top-0 bottom-0 w-10 pointer-events-none z-20 bg-gradient-to-r from-card to-transparent"
          style={{ left: leftOffset }}
        />
      )}
      {showRight && (
        <div className="absolute right-0 top-0 bottom-0 w-10 pointer-events-none z-20 bg-gradient-to-l from-card to-transparent" />
      )}
    </div>
  );
}

// ============================================================================
// BudgetInput — click-to-edit budget value with mobile numeric keyboard
// ============================================================================

function BudgetInput({
  value,
  onChange,
  min,
  max,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setDraft(value.toFixed(1));
    setEditing(true);
  };

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const commit = () => {
    setEditing(false);
    const parsed = Number.parseFloat(draft);
    if (!Number.isNaN(parsed) && Number.isFinite(parsed)) {
      onChange(parsed);
    }
  };

  if (editing) {
    return (
      <div className="flex-1 text-center">
        <div className="inline-flex items-center gap-0.5 font-display text-3xl font-bold">
          <span className="text-foreground">$</span>
          <input
            ref={inputRef}
            type="number"
            inputMode="decimal"
            step="0.1"
            min={min}
            max={max}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") setEditing(false);
            }}
            className="w-20 bg-transparent border-b-2 border-primary text-center text-foreground outline-none font-display text-3xl font-bold [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-foreground">M</span>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={startEditing}
      className="flex-1 text-center cursor-text group"
      aria-label={`${m.f1_apex_budget_cap_aria()}: $${value.toFixed(1)}M`}
    >
      <span className="font-display text-3xl font-bold text-foreground group-hover:text-primary transition-colors border-b-2 border-transparent group-hover:border-primary/50">
        ${value.toFixed(1)}M
      </span>
    </button>
  );
}
